import { useState } from "react";

import { TouchableOpacity, View } from "react-native";
import {
  Icon,
  Modal,
  Portal,
  useTheme,
  Text,
  Divider,
  Button,
} from "react-native-paper";
import { useTailwind } from "tailwind-rn";

import { Picker } from "../../../../components";
import { ReadyForPickupEstimate } from "../../types";

type Props = {
  visible: boolean;
  onDismiss: () => void;
  onSave: (estimatedHours: string) => Promise<void>;
  currentEstimate: ReadyForPickupEstimate;
};

export default function ReadyForPickupEstimateDrawer({
  visible,
  currentEstimate,
  onDismiss,
  onSave,
}: Props) {
  const tailwind = useTailwind();
  const { colors } = useTheme();

  const prefilledValue =
    currentEstimate.calculation_mode === "merchant"
      ? currentEstimate.time_text.replace("h", "")
      : null;

  const [selectedEstimate, setSelectedEstimate] = useState(prefilledValue);
  const [isLoading, setIsLoading] = useState(false);

  return (
    <Portal>
      <Modal
        visible={visible}
        onDismiss={onDismiss}
        style={tailwind("flex-col justify-end mb-0")}
        contentContainerStyle={[
          tailwind("p-4 rounded-t-xl gap-4 relative"),
          { backgroundColor: colors.surface },
        ]}
      >
        <CloseButton onPress={onDismiss} />

        <View style={tailwind("gap-2")}>
          <Text variant="titleMedium" style={tailwind("font-bold mr-8")}>
            ¿Cuánto tardas en tener un pedido listo para recoger?
          </Text>
          <Divider />
        </View>

        <View style={tailwind("gap-2")}>
          <Text>
            Ingresa el tiempo promedio para poderle dar un mejor estimado al
            cliente:
          </Text>
          <Picker
            options={pickerOptions}
            preselectedValue={prefilledValue}
            selectOption={(opt) => setSelectedEstimate(opt.value)}
          />
        </View>

        <Button
          mode="contained"
          style={tailwind("self-center mb-4")}
          disabled={!selectedEstimate || isLoading}
          loading={isLoading}
          onPress={async () => {
            if (!selectedEstimate) {
              onDismiss();
              return;
            }
            try {
              setIsLoading(true);
              await onSave(selectedEstimate);
            } finally {
              setIsLoading(false);
            }
          }}
        >
          {isLoading ? "Guardando..." : "Guardar"}
        </Button>
      </Modal>
    </Portal>
  );
}

function CloseButton({ onPress }: { onPress: () => void }) {
  const tailwind = useTailwind();
  const { colors } = useTheme();
  return (
    <TouchableOpacity
      style={[
        tailwind("absolute top-4 right-4 z-50 rounded-full p-1"),
        { backgroundColor: colors.surfaceDisabled },
      ]}
      onPress={onPress}
    >
      <Icon source="close" size={16} color={colors.onSurfaceDisabled} />
    </TouchableOpacity>
  );
}

const pickerOptions = [
  opt(1),
  opt(2),
  opt(3),
  opt(4),
  opt(5),
  opt(6),
  opt(8),
  opt(12),
  opt(24),
  opt(48),
  opt(72),
];

function opt(n: number) {
  return { label: `${n}h`, value: n.toString() };
}
